import { Button, IconButton, Typography, useMediaQuery } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { FC, useState } from "react";
import CustomCard from "../../../components/card";
import IconsAction from "./components/iconsAction";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import net from "../../../data/network.json";
import {
  wrapperChange,
  wrapperCoin,
  wrapperMain,
  wrapperSwap,
  wrapperSwapInfo,
  wrapperSwapItem,
} from "./swap.style";
import Routes from "../routes";
import useAppSelector from "../../../hooks/useSelector";
import useAppDispatch from "../../../hooks/useDispatch";
import { setFromValueData } from "../../../redux/slices/coinFromValueSlice";
import { setSelectedExchangeData } from "../../../redux/slices/selectedExchangeSlice";
import {
  setExchangeResData,
  setRateId,
} from "../../../redux/slices/exchangeResSlice";
import Wallet from "../wallet";
import {
  setNetIconFromData,
  setNetIconToData,
} from "../../../redux/slices/netIconSlice";
import { validateAddressService } from "../../../services/validAddress";
import { useMutation } from "@tanstack/react-query";
import {
  setValidAddressFromData,
  setValidAddressToData,
} from "../../../redux/slices/walletAddress";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useLocation, useNavigate } from "react-router-dom";
import { baseImageUrl } from "../../../config/imageUrl";

interface IProps {
  setSwitch: (arg0: number) => void;
  setCoinType: (arg0: number | undefined) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  swapDetail: boolean;
  setSwapDetail: (swapDetail: boolean) => void;
  handleChange: any;
  active: number;
  setActive: (active: number) => void;
  showWallet: boolean;
  setShowWallet: (showWallet: boolean) => void;
  confirm: boolean;
  setConfirm: (confirm: boolean) => void;
  historyRequestId: string;
  setHistoryRequestId: (historyRequestId: string) => void;
  price: any;
  setPrice: any;
  timer: any;
  loadingSkeleton: boolean;
  errorMessage: boolean;
}

const Swap: FC<IProps> = ({
  setSwitch,
  setCoinType,
  loading,
  setLoading,
  swapDetail,
  setSwapDetail,
  handleChange,
  active,
  setActive,
  showWallet,
  setShowWallet,
  confirm,
  setConfirm,
  historyRequestId,
  setHistoryRequestId,
  price,
  timer,
  loadingSkeleton,
  errorMessage,
}) => {
  const [fromValue, setFromValue] = useState("");
  const [rateChange, setRateChange] = useState(true);
  const dispatch = useAppDispatch();
  const { coinData } = useAppSelector((state) => state.coinSelect);
  const { netIconFrom } = useAppSelector((state) => state.netIcon);
  const { netIconTo } = useAppSelector((state) => state.netIcon);
  const [fromAddressError, setFromAddressError] = useState("");
  const [toAddressError, setToAddressError] = useState("");
  const isMobileActive = useMediaQuery("(max-width: 1006px)");
  const location = useLocation();
  const navigate = useNavigate();

  const validateAddressQuery = useMutation({
    mutationFn: validateAddressService,
  });

  const fromValueData = useAppSelector(
    (state) => state.coinFromValue.fromValue
  );
  const selectedExchangeData = useAppSelector(
    (state) => state.selectedExchange
  );

  const toWalletAddressData = useAppSelector(
    (state) => state.walletAddress.toWalletAddress
  );

  const fromWalletAddressData = useAppSelector(
    (state) => state.walletAddress.fromWalletAddress
  );

  const exchangeResData = useAppSelector((state) => state.exchangeRes);

  const handleSwap = (id: number) => {
    setSwitch(2);
    dispatch(setNetIconFromData(""));
    dispatch(setNetIconToData(""));
    if (id === 1) {
      setCoinType(1);
    } else if (id === 2) {
      setCoinType(2);
    }
  };

  const handleFromValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const re = /^[0-9]*(\.)?[0-9]*$/;
    if (re.test(e.target.value)) {
      setLoading(true);
      setActive(0);
      dispatch(setExchangeResData());
      dispatch(setRateId());
      setFromValue(e.target.value);
      dispatch(setFromValueData(e.target.value));
      dispatch(setSelectedExchangeData());
      const searchParams = new URLSearchParams(location.search);
      searchParams.set("amount", e.target.value);
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    } else {
      return false;
    }
  };

  //address
  const validateAddress = async (
    address: string,
    networkName: string | undefined,
    setError: (msg: string) => void,
    setValid: (valid: boolean) => void,
    isToAddress: boolean
  ) => {
    if (!address) {
      setError("");
      setValid(true);
      return;
    }

    const selectedNetwork = net.find(
      (item) => item?.name.toUpperCase() === networkName?.toUpperCase()
    )?.name;

    const param = { address, network: selectedNetwork };

    try {
      const data = await validateAddressQuery.mutateAsync(param);
      if (data?.data?.message === "OK") {
        if (data?.data?.valid) {
          setValid(true);
          setError("");
        } else {
          setValid(false);
          setError(
            isToAddress
              ? "Invalid recipient address. Please check and try again."
              : "Invalid refund address. Please check and try again."
          );
        }
      } else {
        setValid(true);
        setError("");
      }
    } catch (error) {
      console.error("Address validation failed:", error);
      if (address.length <= 1) {
        setValid(false);
        setError(
          isToAddress
            ? "Invalid recipient address. Please check and try again."
            : "Invalid refund address. Please check and try again."
        );
      } else {
        setValid(true);
        setError("");
      }
    }
  };

  const handleSwapDetails = async () => {
    if (toWalletAddressData) {
      await validateAddress(
        toWalletAddressData,
        coinData[1]?.network,
        setToAddressError,
        (valid) => dispatch(setValidAddressToData(valid)),
        true
      );
    } else {
      setToAddressError("");
    }

    if (fromWalletAddressData) {
      await validateAddress(
        fromWalletAddressData,
        coinData[0]?.network,
        setFromAddressError,
        (valid) => dispatch(setValidAddressFromData(valid)),
        false
      );
    } else {
      setFromAddressError("");
    }

    setShowWallet(true);
  };

  const handleRateChange = () => {
    setRateChange(!rateChange);
  };

  return (
    <>
      {!showWallet ? (
        <Grid sx={wrapperMain}>
          <CustomCard>
            <Grid sx={wrapperSwap}>
              <IconsAction setSwitch={setSwitch} />
              <Typography variant="h4" mb={2}>
                Swap
              </Typography>
              <Grid sx={wrapperSwapItem} mb={2}>
                <span>From</span>
                <Grid container sx={wrapperSwapInfo} gap={2}>
                  <Grid
                    sx={{
                      background: (theme) => theme?.palette?.secondary?.light,
                      minWidth: "190px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSwap(1)}
                  >
                    <Grid container pr={3}>
                      <Grid mr={1} style={{ position: "relative" }}>
                        <img
                          src={
                            coinData[0]?.image
                              ? coinData[0].image.startsWith("http")
                                ? coinData[0].image
                                : `${baseImageUrl}/${coinData[0].image}`
                              : "./assets/images/coins/noPic.svg"
                          }
                          alt="flashift"
                          width="42px"
                          height="42px"
                        />
                        <img
                          alt="flashift"
                          src={
                            netIconFrom
                              ? netIconFrom
                              : `${baseImageUrl}/${
                                  net.filter(
                                    (item) =>
                                      item?.name.toUpperCase() ===
                                      coinData[0]?.network.toUpperCase()
                                  )?.[0]?.image
                                }`
                          }
                          width="20px"
                          height="20px"
                          className="netIcon"
                          style={{ left: "28px" }}
                        />
                      </Grid>
                      <Grid sx={wrapperCoin}>
                        <Typography variant="h6">
                          {coinData[0]?.symbol?.toUpperCase()}
                        </Typography>
                        <Typography>
                          {net
                            .filter(
                              (item) =>
                                item?.name.toUpperCase() ===
                                coinData[0]?.network.toUpperCase()
                            )?.[0]
                            ?.name.toUpperCase()}
                        </Typography>
                      </Grid>
                    </Grid>
                    <ExpandMoreIcon />
                  </Grid>
                  <Grid>
                    <input
                      type="text"
                      value={fromValueData ? fromValueData : fromValue}
                      onChange={(event) => handleFromValue(event)}
                      autoComplete="off"
                      inputMode="decimal"
                      placeholder={isMobileActive ? "Enter an Amount" : ""}
                      autoFocus
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid sx={wrapperChange}>
                <IconButton onClick={handleChange}>
                  <img src="./assets/images/swap/change.svg" alt="flashift" />
                </IconButton>
              </Grid>
              <Grid sx={wrapperSwapItem} mb={2}>
                <span>To</span>
                <Grid container sx={wrapperSwapInfo} gap={2}>
                  <Grid
                    sx={{
                      background: (theme) => theme?.palette?.secondary?.light,
                      minWidth: "190px",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSwap(2)}
                  >
                    <Grid container pr={3}>
                      <Grid mr={1} style={{ position: "relative" }}>
                        <img
                          src={
                            coinData[1]?.image
                              ? coinData[1].image.startsWith("http")
                                ? coinData[1].image
                                : `${baseImageUrl}/${coinData[1].image}`
                              : "./assets/images/coins/noPic.svg"
                          }
                          alt="flashift"
                          width="42px"
                          height="42px"
                        />
                        <img
                          alt="flashift"
                          src={
                            netIconTo
                              ? netIconTo
                              : `${baseImageUrl}/${
                                  net.filter(
                                    (item) =>
                                      item?.name.toUpperCase() ===
                                      coinData[1]?.network.toUpperCase()
                                  )?.[0]?.image
                                }`
                          }
                          width="20px"
                          height="20px"
                          className="netIcon"
                          style={{ left: "28px" }}
                        />
                      </Grid>
                      <Grid sx={wrapperCoin}>
                        <Typography variant="h6">
                          {coinData[1]?.symbol?.toUpperCase()}
                        </Typography>
                        <Typography>
                          {net
                            .filter(
                              (item) =>
                                item?.name.toUpperCase() ===
                                coinData[1]?.network.toUpperCase()
                            )?.[0]
                            ?.name.toUpperCase()}
                        </Typography>
                      </Grid>
                    </Grid>
                    <ExpandMoreIcon />
                  </Grid>
                  {!loadingSkeleton && (
                    <Grid>
                      <input
                        value={
                          !!selectedExchangeData?.selectedExchange?.amount
                            ? selectedExchangeData?.selectedExchange?.amount.toFixed(
                                5
                              )
                            : exchangeResData?.exchangeRes &&
                              ((fromValue && fromValue !== "0") ||
                                (fromValueData && fromValueData !== "0")) &&
                              !!exchangeResData?.exchangeRes?.max_amount &&
                              exchangeResData?.exchangeRes?.max_amount !== "-1"
                            ? exchangeResData?.exchangeRes?.data?.[0]?.amount.toFixed(
                                5
                              )
                            : ""
                        }
                        type="text"
                        readOnly
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {!loadingSkeleton &&
                exchangeResData &&
                exchangeResData?.exchangeRes?.data &&
                exchangeResData?.exchangeRes?.data.length > 0 && (
                  <Grid
                    sx={{
                      background: (theme) => theme?.palette?.secondary?.light,
                      fontSize: "1.2rem",
                      borderRadius: "8px",
                      justifyContent: "space-between",
                    }}
                    className="rate animate__animated animate__fadeIn"
                    p={1}
                    mb={2}
                    container
                  >
                    <Grid pt={1}>
                      {rateChange ? (
                        <>
                          {" "}
                          1 {coinData[0]?.symbol.toUpperCase()}
                          <IconButton onClick={handleRateChange}>
                            <img
                              src="./assets/images/swap/rateChange.svg"
                              alt="flashift"
                              style={{ transform: "rotate(90deg)" }}
                            />
                          </IconButton>
                          {""}
                          {(
                            Number(
                              selectedExchangeData?.selectedExchange?.amount
                            ) / Number(fromValueData)
                          ).toFixed(8) === "0.00000000"
                            ? (
                                Number(
                                  selectedExchangeData?.selectedExchange?.amount
                                ) / Number(fromValueData)
                              ).toFixed(16)
                            : (
                                Number(
                                  selectedExchangeData?.selectedExchange?.amount
                                ) / Number(fromValueData)
                              ).toFixed(8)}
                          {""} {coinData[1]?.symbol.toUpperCase()}
                        </>
                      ) : (
                        <>
                          {" "}
                          1 {coinData[1]?.symbol.toUpperCase()}
                          <IconButton onClick={handleRateChange}>
                            <img
                              src="./assets/images/swap/rateChange.svg"
                              alt="flashift"
                              style={{ transform: "rotate(90deg)" }}
                            />
                          </IconButton>
                          {""}
                          {(
                            Number(fromValueData) /
                            Number(
                              selectedExchangeData?.selectedExchange?.amount
                            )
                          ).toFixed(8) === "0.00000000"
                            ? (
                                Number(fromValueData) /
                                Number(
                                  selectedExchangeData?.selectedExchange?.amount
                                )
                              ).toFixed(16)
                            : (
                                Number(fromValueData) /
                                Number(
                                  selectedExchangeData?.selectedExchange?.amount
                                )
                              ).toFixed(8)}
                          {""} {coinData[0]?.symbol.toUpperCase()}
                        </>
                      )}
                    </Grid>
                    <Typography pt={1.8}>
                      $ ~{" "}
                      {(
                        price * selectedExchangeData?.selectedExchange?.amount
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                    <Grid className="all_fees" container>
                      <ErrorOutlineIcon style={{ width: "17px" }} />
                      <span
                        style={{
                          position: "relative",
                          top: "-2.5px",
                          marginLeft: "3px",
                        }}
                      >
                        All Fees Included
                      </span>
                    </Grid>
                  </Grid>
                )}
              <Button
                disabled={loading || loadingSkeleton || errorMessage}
                variant="contained"
                fullWidth
                onClick={handleSwapDetails}
                className="swap_button"
              >
                {!exchangeResData?.exchangeRes?.message &&
                !(fromValue || fromValueData) ? (
                  "Enter an Amount"
                ) : loading && exchangeResData?.exchangeRes?.message ? (
                  "Error"
                ) : (loading && !exchangeResData?.exchangeRes?.message) ||
                  loadingSkeleton ? (
                  <>
                    <img
                      className="loading"
                      src="/assets/images/loading.svg"
                      alt="flashift"
                    />
                    Waiting...
                  </>
                ) : !loading &&
                  ((exchangeResData?.exchangeRes?.data &&
                    exchangeResData.exchangeRes.data.length > 0) ||
                    selectedExchangeData?.selectedExchange) ? (
                  <>
                    Swap via
                    <img
                      style={{
                        width: "17px",
                        position: "relative",
                        marginLeft: "4px",
                      }}
                      src={
                        selectedExchangeData?.selectedExchange
                          ?.exchange_type === "floating"
                          ? "/assets/images/swap/buttonfloat.svg"
                          : "/assets/images/swap/buttonfixed.png"
                      }
                      alt="Exchange Type Icon"
                    />
                    {` ${selectedExchangeData?.selectedExchange?.partner_name}`}
                  </>
                ) : (
                  "Error"
                )}
              </Button>
            </Grid>
          </CustomCard>
          {(fromValue || fromValueData) &&
            (Number(fromValue) > 0 || Number(fromValueData) > 0) && (
              <Routes
                loading={loading}
                active={active}
                setActive={setActive}
                price={price}
                timer={timer}
                loadingSkeleton={loadingSkeleton}
              />
            )}
        </Grid>
      ) : (
        <Wallet
          setShowWallet={setShowWallet}
          swapDetail={swapDetail}
          setSwapDetail={setSwapDetail}
          confirm={confirm}
          setConfirm={setConfirm}
          historyRequestId={historyRequestId}
          setHistoryRequestId={setHistoryRequestId}
          setSwitch={setSwitch}
          setLoading={setLoading}
          timer={timer}
          loading={loading}
          fromAddressError={fromAddressError}
          setFromAddressError={setFromAddressError}
          toAddressError={toAddressError}
          setToAddressError={setToAddressError}
          setActive={setActive}
        />
      )}
    </>
  );
};
export default Swap;
